import type { ReactNode } from 'react';
import { useMaxWidth, BP } from '@/shared/hooks/useMediaQuery';
import { useSoftphoneWindow } from '@/personas/agent/stores/softphoneWindow';
import { Icon } from '@/components/ui/Icon';

/**
 * Content + sticky softphone column. Above the `stack` breakpoint they sit
 * side by side (content | softphone); below it the softphone moves to the top
 * full-width.
 *
 * When the softphone is detached (floating over all screens), the docked column
 * is replaced by a slim placeholder with a "return to screen" action, and the
 * content takes the full width.
 */
export function SoftphoneLayout({ content, softphone }: { content: ReactNode; softphone: ReactNode }) {
  const stacked = useMaxWidth(BP.stack);
  const floating = useSoftphoneWindow((s) => s.floating);
  const setFloating = useSoftphoneWindow((s) => s.setFloating);

  // Floating: the global FloatingSoftphone renders the live phone; here just
  // give the content the whole page (with a hint to bring it back).
  if (floating) {
    return (
      <div className="stack" style={{ gap: 'var(--gap)' }}>
        <button
          type="button"
          className="btn ghost"
          onClick={() => setFloating(false)}
          style={{ alignSelf: 'flex-start', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}
        >
          <Icon name="target" size={13} /> הסופטפון צף — החזר למסך
        </button>
        <div>{content}</div>
      </div>
    );
  }

  if (stacked) {
    return (
      <div className="stack" style={{ gap: 'var(--gap)' }}>
        <div>{softphone}</div>
        <div>{content}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) var(--softphone-w)',
        gap: 'var(--gap)',
        alignItems: 'flex-start',
      }}
    >
      <div className="stack">{content}</div>
      <div style={{ position: 'sticky', top: 16 }}>{softphone}</div>
    </div>
  );
}
